/**
 * Why a message looks forged.
 *
 * Not a spam filter — a spam filter guesses, and a guess that fires on real
 * mail teaches you to click past it. Everything here is EVIDENCE: a verdict
 * your own mail server already reached, or a construct in the message that
 * legitimate mail does not contain. Each finding names what was found and what
 * it means, so the reader can check it rather than trust a score.
 *
 * Written 2026-08-25 after two phishes landed looking clean in the reader
 * (Bob: "this is an attack — could you do a better job of identifying it as
 * bogus?"). Both carried all of the following, and mailx surfaced NONE of it:
 *
 *   X-Spam-Flag: YES                        <- his own server, score 6.7/4.5
 *   Authentication-Results: ... spf=fail
 *   From: bobip@bobf.frankston.com          <- his own address, forged
 *   To:   bobip@bobf.frankston.com
 *   Return-Path: <3EDTkWNvbY@visionsurveysqld.com.au>
 *   <area coords="0,0,10000,10000" href="http://.../redirect.php?newurl=...">
 *
 * No header in that list was read anywhere in the codebase, and the one
 * impersonation check that existed (displayNameClaimsOtherAddress) needs a
 * display name, which these messages did not have. The whole visible card was
 * one hidden link through an open redirector, with the victim's own address
 * base64'd into the fragment so the landing page could prefill it.
 *
 * Deliberately NOT included: bare SPF failure on mail that isn't claiming to
 * be you. Forwarded mail fails SPF constantly, and flagging that would be
 * exactly the cry-wolf banner this is trying not to be.
 */

/** What the reader can vouch for, and the allowlist.jsonc key each one is
 *  written to. One table, shared by the desktop service and the Android
 *  web-service, so "trust X" cannot land in different keys on the two
 *  platforms — the merge is by key. (Claude Code 2026-09-09; the two
 *  services each carried their own ternary before, and adding `list` to one
 *  and not the other is exactly the drift this prevents.) */
export const TRUST_LIST_KEYS = {
    sender: "trustedSenders",
    domain: "trustedDomains",
    intermediary: "trustedIntermediaries",
    list: "trustedLists",
} as const;
export type TrustListType = keyof typeof TRUST_LIST_KEYS;

/** One piece of evidence that a message is not what it claims to be. */
export interface TrustFinding {
    /** Stable identifier — for tests, logs, and any future suppression UI. */
    id: "server-spam-verdict" | "self-spoof" | "relay-auth-mismatch"
        | "zero-width-obfuscation" | "hidden-link-overlay" | "redirector-link";
    /** `danger`: near-certain forgery. `caution`: worth a second look.
     *  `info`: something the server said, in a register that is not an
     *  accusation — a bulk-mail verdict on a sender DMARC proved. */
    severity: "danger" | "caution" | "info";
    /** One line, naming what was found. Never "this message is suspicious". */
    text: string;
    /** The specific evidence — a header value, a host, a score. */
    detail: string;
    /** A party that provably handled this message and could be vouched for
     *  as an intermediary (allowlist.jsonc `trustedIntermediaries`) — the
     *  DKIM signer behind a redirector, or the Sender: domain that signed on
     *  the author's behalf. Set only when such a party exists and is not yet
     *  trusted, so the viewer can offer the action by name. (2026-09-08) */
    via?: string;
    /** The mailing list that provably delivered this message (its List-Id),
     *  when the list is not yet in allowlist.jsonc `trustedLists`, so the
     *  viewer can offer "Trust the ‹list› list". (2026-09-09) */
    list?: string;
}

export interface TrustInput {
    /** Every header line, in order, as mailparser's `headerLines`. Repeated
     *  headers matter: Authentication-Results and Received both appear more
     *  than once and the interesting one is not always the first. */
    headerLines: { key: string; line: string }[];
    /** Envelope From address. */
    fromAddress: string;
    /** Addresses belonging to the reader — their own aliases. Used only to
     *  recognise a message claiming to BE them. */
    ownAddresses: string[];
    /** Sanitized HTML body, for the link-construct checks. */
    bodyHtml: string;
    /** The reader has approved this sender or its domain in allowlist.jsonc —
     *  "Always: *@icecer.com" in the remote-content banner, or "Trust all
     *  senders at ‹domain›" in the address menu. It answers the only question
     *  a spam score cannot: is this mail WANTED. It never silences forgery
     *  evidence, and it counts only on a message whose sender was actually
     *  proved — otherwise the list would be a switch a forger could flip by
     *  typing a trusted address into From. */
    senderTrusted?: boolean;
    /** Plain-text body, for the character-level checks. Separate from the HTML
     *  because markup legitimately contains things prose does not, and the
     *  zero-width test would trip over an entity or an attribute value. */
    bodyText?: string;
    /** Domains the reader trusts to carry mail on OTHER people's behalf —
     *  Google Calendar, Zoom, DocuSign — from allowlist.jsonc
     *  `trustedIntermediaries`. Bob 2026-09-08, on a Google Calendar
     *  invitation whose every link is wrapped in google.com/url?q=…: "how do
     *  I mark zoom and sites like that as trusted intermediaries?" An entry
     *  counts only on a message the intermediary PROVABLY handled (a
     *  dkim=pass for its domain, or a DMARC pass when it is the From) — an
     *  open redirector on google.com is the classic laundering hop, and a
     *  list that cleared it on sight would be a switch any forger could
     *  flip by pasting the link. See provedIntermediary. */
    trustedIntermediaries?: string[];
    /** Mailing lists the reader subscribes to, by List-Id ("ieee-cs.mit.edu"),
     *  from allowlist.jsonc `trustedLists`. Bob 2026-09-09: "how do I say
     *  that this is a valid mailing list message". A list that does not
     *  rewrite From (Mailman only does so when the author's domain publishes
     *  a DMARC policy; gmail.com does not) delivers the poster's own address,
     *  and its subject tag and footer break every signature on the way — so
     *  the author can never be proved, and per-sender trust has nothing to
     *  hold on to. The LIST can be proved: the receiving server records
     *  spf=pass for the list's bounce address, or dkim=pass for its domain.
     *  An entry counts only on such a message. See listProof. */
    trustedLists?: string[];
}

/** First value of a header, case-insensitive. */
function header(lines: TrustInput["headerLines"], name: string): string {
    const want = name.toLowerCase();
    for (const h of lines) {
        if ((h.key || "").toLowerCase() !== want) continue;
        const colon = h.line.indexOf(":");
        return colon < 0 ? "" : h.line.slice(colon + 1).trim();
    }
    return "";
}

/** All values of a repeated header. */
function headerAll(lines: TrustInput["headerLines"], name: string): string[] {
    const want = name.toLowerCase();
    const out: string[] = [];
    for (const h of lines) {
        if ((h.key || "").toLowerCase() !== want) continue;
        const colon = h.line.indexOf(":");
        if (colon >= 0) out.push(h.line.slice(colon + 1).trim());
    }
    return out;
}

function bare(addr: string): string {
    const m = (addr || "").match(/[^\s<>,;]+@[^\s<>,;]+/);
    return (m ? m[0] : addr || "").toLowerCase().replace(/[.,;]+$/, "");
}

/** Do two hostnames belong to the same organisation?
 *
 *  Equality or a sub/parent-domain relation, never "the last two labels
 *  match": that would make paypal.co.uk and evil.co.uk relatives, which is
 *  the case this whole file exists to catch. A subdomain relationship cannot
 *  be forged across organisations, so it is safe in the other direction.
 *  Same test displayNameClaimsOtherAddress uses on addresses. */
function sameOrg(a: string, b: string): boolean {
    const x = (a || "").toLowerCase();
    const y = (b || "").toLowerCase();
    if (!x || !y) return false;
    return x === y || x.endsWith("." + y) || y.endsWith("." + x);
}

/**
 * What SpamAssassin's rules are evidence OF — which is not what its score is.
 *
 * The score answers "should this be delivered". The banner asks a different
 * question, "is this forged", and the `tests=` list that separates the two
 * sits in the same header and was never read. Four messages Bob subscribed to
 * (Constant Contact, Adobe MAX, Mokin, ActBlue) scored 2.2 to 10.9 and two of
 * them raised the app's strongest banner; every rule that pushed them there
 * was a bulk-mail test or a third-party blocklist entry about a host the
 * message merely LINKS TO. Mokin's 6.0 — more than the whole 4.5 threshold on
 * its own — was `URIBL_SBL` on the IP of the nameserver of a linked domain:
 * fourth-order guilt by association, saying nothing whatever about whether
 * Mokin sent the mail. All four passed DMARC aligned, so on all four the
 * headline "This message is not what it says it is" was factually false.
 * Full diagnosis: docs/spam-false-positives.md. (Claude Code 2026-08-27)
 *
 * Note what is NOT here: any tuning of a threshold. A rule's class follows
 * from what it tests, never from how much it scored.
 */
type RuleClass = "forgery" | "association" | "bulk" | "neutral" | "other";

/** Rules that say THIS message lies about who sent it. The only class that
 *  earns the red banner. `KAM_DMARC_STATUS` is deliberately absent — it is a
 *  0.0-scoring status marker that fired on Mokin, a message the receiving
 *  server had just recorded as `dmarc=pass`. */
const FORGERY_RULES: RegExp[] = [
    /^SPF_(HELO_)?(FAIL|SOFTFAIL)$/,
    /^(KAM_)?DMARC_(FAIL|REJECT|QUAR)/,
    /^DKIM_ADSP_(ALL|DISCARD|NXDOMAIN)$/,
    /^FORGED_/,
    /SPOOF/,
    /_PHISH/, /PHISHING/, /_MALW/, /MALWARE/,
];

/** Reputation a third party holds about some host the message mentions or
 *  travelled through. Real evidence the mail is unwanted; no evidence at all
 *  that the From line is false — the listed party is usually not the sender. */
const ASSOCIATION_RULES: RegExp[] = [
    /^URIBL_(SBL|BLACK|RED|DBL|ABUSE)/,
    /^SH_DBL/,
    /^SEM_URIBL/,
    /^SPAMHAUS_/,
    /^RCVD_IN_(SBL|XBL|PBL|BL_SPAMCOP|SORBS|BRBL|VALIDITY|MSPIKE_[LZ])/,
    // Blocklist rules that happen to live under other prefixes. KAM_*URIBL_*
    // ("Body contains URI listed in PCCC WILD RBL", 9.0) and SH_HBL_EMAILS
    // ("Email address listed in email blocklist", 8.0) are the same kind of
    // evidence as URIBL_SBL — somebody else's list, about a host or address
    // the message mentions — and they were the bulk of what still cautioned a
    // proved sender: a Business Insider newsletter, an MIT cancer-center
    // invitation, a campaign mailing. KAM_INFOUSMEBIZ judges the TLD a domain
    // lives in, which is guilt by neighbourhood.
    /^KAM_(BODY_|FROM_)?URIBL/, /^SH_HBL/, /^KAM_INFOUSMEBIZ$/,
];

/** The shape of mass mailing: bulk-detection networks, marketing markup, list
 *  plumbing. A score built entirely from these is a bulk verdict however high
 *  it climbs — Adobe MAX reached 3.9 without one rule that looks at identity. */
const BULK_RULES: RegExp[] = [
    /^DCC_/, /^PYZOR_/, /^RAZOR2_/,
    /^MAILING_LIST/, /^LIST_/,
    // KAM_* by name only, never the whole family: it holds marketing and list
    // rules alongside scam-CONTENT rules (KAM_BENEFICIARY is worth 10.0,
    // KAM_FAKE_NORTONLOW 6.5), and calling those bulk would quiet the loudest
    // true positives in the store.
    /^KAM_(BODY_)?MARKETINGBL/, /^KAM_UNSUB/, /^KAM_TRACKIMAGE$/,
    /^KAM_REALLYHUGEIMGSRC$/, /^KAM_DMARC_STATUS$/, /^KAM_COUPON/,
    /^HTML_/, /^MIME_HTML/, /^WORD_INVIS$/,
    /^URIBL_(GREY|CSS|BLOCKED|CT_SURBL)/, /^SH_BODYURI/,
    /^HEADER_FROM_DIFFERENT_DOMAINS$/,          // every ESP on earth trips this
    /^MPART_ALT_DIFF/, /^TVD_/, /^T_REMOTE_IMAGE$/, /^UNPARSEABLE_RELAY$/,
    // Branded ESP click-trackers. Not open redirectors (URI_PHP_REDIR and
    // friends stay unclassified): these are the sending platform's own
    // tracking domain, which is mass-mail machinery by definition.
    /^GB_AWSTRACK/, /AWSTRACK/, /^GB_SENDGRID/, /^GB_MAILCHIMP/,
];

/** Passes, whitelistings, and Bayes' own opinion — informational, and in
 *  BAYES_00's case exculpatory. `DKIM_INVALID` lives here rather than in the
 *  forgery class because a broken signature is routine on relayed and
 *  list-rewritten mail (Mokin, sent via Shopify and SendGrid, carries it while
 *  the receiving server records dkim=pass). It is promoted to forgery only
 *  when DMARC also failed — see classifyRule. */
const NEUTRAL_RULES: RegExp[] = [
    /^BAYES_/,
    /^SPF_(PASS|NONE|HELO_(PASS|NONE|NEUTRAL))$/,
    /^DKIM_(SIGNED|VALID|VALID_AU|VALID_EF|INVALID)$/,
    /^DKIMWL_/, /^RCVD_IN_(DNSWL|MSPIKE_H|IADB)/,
    /^SHORTCIRCUIT$/, /^AWL$/, /^NO_RELAYS$/, /^ALL_TRUSTED$/,
];

/** Rules that test whether the From domain matches the path the message took
 *  — which a mailing list breaks BY DESIGN. Mailman re-sends the poster's
 *  message from its own host with its own bounce address, so a Gmail From
 *  arrives without a Google hop or a Google envelope, and the author's
 *  signature no longer verifies over the tagged subject and appended footer.
 *  Every one of these fired on an ordinary IEEE-CS meeting announcement
 *  (Bob 2026-09-09) and together they painted it as a phish. They are
 *  evidence of forgery only when nothing else explains the mismatch; on a
 *  message a list PROVABLY delivered (see listProof) the list is the
 *  explanation, and they are set to neutral. URL and phishing listings are
 *  not in this set: a list member can post a bad link like anyone else. */
const LIST_RELAY_RULES: RegExp[] = [
    /^POSSIBLE_GMAIL_PHISHER$/,     // From gmail.com, not sent through Google
    /^FREEMAIL_FORGED_FROMDOMAIN$/, // From and envelope are different freemail domains
    /^(NML_|DKIM_)ADSP_/,           // author domain did not sign what arrived
    /^FORGED_[A-Z]+_RCVD$/,         // From ‹provider› without ‹provider›'s Received
];

function classifyRule(name: string, dmarcFailed: boolean, listRelayed = false): RuleClass {
    if (listRelayed) for (const re of LIST_RELAY_RULES) if (re.test(name)) return "neutral";
    if (dmarcFailed && name === "DKIM_INVALID") return "forgery";
    for (const re of FORGERY_RULES) if (re.test(name)) return "forgery";
    for (const re of ASSOCIATION_RULES) if (re.test(name)) return "association";
    for (const re of NEUTRAL_RULES) if (re.test(name)) return "neutral";
    for (const re of BULK_RULES) if (re.test(name)) return "bulk";
    return "other";
}

/**
 * Did the receiving server prove the From domain?
 *
 * Only the TOPMOST Authentication-Results is read. Headers are prepended, so
 * the first one is the one our own server wrote; every one below it travelled
 * with the message and a forger can write whatever they like there. Same
 * asymmetry serverSpamVerdict relies on, in the other direction: a claim that
 * SILENCES a warning must come from a source the sender cannot control.
 *
 * `dmarc=pass` is aligned by definition (RFC 7489) — SPF or DKIM passed on the
 * same domain the reader sees in From. The `header.from=` it names is checked
 * against the actual From line anyway, because an Authentication-Results about
 * some other message proves nothing about this one.
 */
/** Domains whose DKIM signature the receiving server verified, from the
 *  topmost Authentication-Results — the one the sender cannot write. A
 *  signer is a party that provably handled the message: it need not be the
 *  author (Google Calendar signs d=google.com on an invitation From the
 *  organizer), which is exactly what makes it an intermediary. */
function dkimSigners(input: TrustInput): string[] {
    const auth = headerAll(input.headerLines, "authentication-results")[0] || "";
    const out: string[] = [];
    for (const part of auth.split(";")) {
        if (!/\bdkim=pass\b/i.test(part)) continue;
        const d = (part.match(/header\.d=([^\s;,]+)/i)?.[1] || "").toLowerCase();
        if (d && !out.includes(d)) out.push(d);
    }
    return out;
}

/** Did the From domain's OWN organisation pass authentication at the
 *  receiving server — a verified signature for the From domain, or an SPF
 *  pass for an envelope at the From domain? This is DMARC's relaxed
 *  alignment computed locally, for domains that publish no DMARC policy and
 *  so never get a dmarc= verdict. Only the From domain's own servers can pass
 *  SPF as that domain or sign as it, so a forger's pass for their own domain
 *  does not count. Returns which proof, or "". (2026-09-09) */
function alignedPass(input: TrustInput): "dkim" | "spf" | "" {
    const fromDomain = (bare(input.fromAddress).split("@")[1] || "").toLowerCase();
    if (!fromDomain) return "";
    if (dkimSigners(input).some(d => sameOrg(d, fromDomain))) return "dkim";
    const auth = headerAll(input.headerLines, "authentication-results")[0] || "";
    for (const part of auth.split(";")) {
        if (!/\bspf=pass\b/i.test(part)) continue;
        const mailfrom = (part.match(/(?:spf|smtp)\.mailfrom=([^\s;,]+)/i)?.[1] || "").toLowerCase();
        const domain = mailfrom.includes("@") ? mailfrom.split("@")[1] : mailfrom;
        if (domain && sameOrg(domain, fromDomain)) return "spf";
    }
    return "";
}

/** The entry in `trustedIntermediaries` that provably handled this message,
 *  or "" — proof being a verified signature from its domain, or a DMARC pass
 *  when it is the author. The list alone proves nothing. (2026-09-08) */
function provedIntermediary(input: TrustInput): string {
    const list = (input.trustedIntermediaries || []).map(d => (d || "").trim().toLowerCase()).filter(Boolean);
    if (!list.length) return "";
    const proved = dkimSigners(input);
    const dmarc = dmarcProof(input);
    if (dmarc.pass && dmarc.domain) proved.push(dmarc.domain);
    for (const entry of list)
        if (proved.some(p => sameOrg(p, entry))) return entry;
    return "";
}

/** The mailing list this message came through, and whether the receiving
 *  server proved the list sent it. (2026-09-09)
 *
 *  Identity is the List-Id (RFC 2919), the one header that is the same on
 *  every post to the list whoever wrote it — `<ieee-cs.mit.edu>` — so it is
 *  what `trustedLists` is keyed on. The list's HOST is the Sender: domain
 *  (Mailman sets `Sender: list-bounces@host`) or the List-Id's own domain.
 *
 *  Proof is the topmost Authentication-Results, which the sender cannot
 *  write: `spf=pass` for a bounce address at the list's host — SPF is checked
 *  against the envelope, and a list's envelope IS the list — or `dkim=pass`
 *  for the list's domain, which Mailman 3 and Sympa add. A List-Id alone is
 *  a header anyone can type; without the server's pass it proves nothing,
 *  and `trustedLists` never counts on such a message. */
function listProof(input: TrustInput): { id: string; proved: boolean; host: string } {
    const none = { id: "", proved: false, host: "" };
    const raw = header(input.headerLines, "list-id");
    if (!raw) return none;
    // `Meeting Announcements <ieee-cs.mit.edu>` or bare `<ieee-cs.mit.edu>`;
    // the angle-bracketed token is the identifier, the rest is a comment.
    const id = (raw.match(/<([^>]+)>/)?.[1] || raw).trim().toLowerCase();
    if (!id) return none;

    const senderDomain = (bare(header(input.headerLines, "sender")).split("@")[1] || "").toLowerCase();
    const hosts = [senderDomain, id].filter(Boolean);
    const auth = headerAll(input.headerLines, "authentication-results")[0] || "";
    for (const part of auth.split(";")) {
        if (/\bspf=pass\b/i.test(part)) {
            // iecc writes `spf.mailfrom=user@host`, Microsoft `smtp.mailfrom=host`.
            const mailfrom = (part.match(/(?:spf|smtp)\.mailfrom=([^\s;,]+)/i)?.[1] || "").toLowerCase();
            const domain = mailfrom.includes("@") ? mailfrom.split("@")[1] : mailfrom;
            if (domain && hosts.some(h => sameOrg(domain, h))) return { id, proved: true, host: domain };
        }
        if (/\bdkim=pass\b/i.test(part)) {
            const d = (part.match(/header\.d=([^\s;,]+)/i)?.[1] || "").toLowerCase();
            if (d && hosts.some(h => sameOrg(d, h))) return { id, proved: true, host: d };
        }
    }
    return { id, proved: false, host: "" };
}

function dmarcProof(input: TrustInput): { pass: boolean; failed: boolean; authFailures: string[]; domain: string; policy: string } {
    const auth = headerAll(input.headerLines, "authentication-results")[0] || "";
    if (!auth) return { pass: false, failed: false, authFailures: [], domain: "", policy: "" };

    // What the server caught failing, by name. An outright authentication
    // failure IS an identity test, whatever the tests= list happens to call
    // it — this is what keeps the 2026-08-25 phishes red under the new
    // classification, since spf=fail is the whole point of them.
    const authFailures = [
        /\bspf=(fail|softfail)\b/i.test(auth) ? "SPF" : "",
        /\bdkim=(fail|permerror)\b/i.test(auth) ? "DKIM" : "",
        /\bdmarc=(fail|permerror)\b/i.test(auth) ? "DMARC" : "",
    ].filter(Boolean);
    const failed = authFailures.includes("DMARC");
    if (!/\bdmarc=pass\b/i.test(auth)) return { pass: false, failed, authFailures, domain: "", policy: "" };

    const stated = (auth.match(/header\.from=([^\s;,]+)/i)?.[1] || "").toLowerCase();
    const policy = (auth.match(/policy\.dmarc=([^\s;,]+)/i)?.[1] || "").toLowerCase();
    const fromDomain = (bare(input.fromAddress).split("@")[1] || "").toLowerCase();
    if (stated && fromDomain
        && stated !== fromDomain
        && !stated.endsWith("." + fromDomain)
        && !fromDomain.endsWith("." + stated)) {
        return { pass: false, failed, authFailures, domain: "", policy };
    }
    return { pass: true, failed: false, authFailures, domain: stated || fromDomain, policy };
}

/** One rule that fired, with its score and description when the server left a
 *  report. `X-Spam-Report` appears only on flagged mail, so score and
 *  description are often absent while the name always is not. */
interface SpamRule {
    name: string;
    score: number;
    description: string;
    cls: RuleClass;
}

/** Unfold a header value the way RFC 5322 folds it: delete the line break,
 *  KEEP the whitespace that continues the line. SpamAssassin breaks its tests=
 *  list right after a comma, so that continuation whitespace is all that
 *  separates two rule names from being glued into one — the list is de-spaced
 *  after it has been cut out, not here. X-Spam-Report is NOT unfolded: there
 *  the line structure is the record structure. */
function unfold(value: string): string {
    return value.replace(/\r?\n/g, "");
}

/** Per-rule scores and descriptions out of X-Spam-Report, which reads:
 *
 *      *  6.0 URIBL_SBL Contains an URL's NS IP listed in the Spamhaus SBL
 *      *      blocklist
 *      *      [URI: ns12.xincache.com/112.80.181.111]
 *
 *  The bracketed evidence lines are dropped: they name the third party, and
 *  the finding is about this message. */
function parseSpamReport(report: string): Map<string, { score: number; description: string }> {
    const out = new Map<string, { score: number; description: string }>();
    let current: { score: number; description: string } = null;
    for (const raw of report.split(/\r?\n/)) {
        const line = raw.replace(/^[ \t]*\*?[ \t]*/, "").trimEnd();
        if (!line) continue;
        const start = line.match(/^(-?\d+(?:\.\d+)?)\s+([A-Z0-9_]{3,})\s*(.*)$/);
        if (start) {
            current = { score: Number(start[1]), description: start[3].trim() };
            out.set(start[2], current);
            continue;
        }
        if (current && !line.startsWith("[")) current.description += " " + line;
    }
    for (const rule of out.values())
        rule.description = rule.description
            .replace(/\s+/g, " ")
            .replace(/^(BODY|RAW|HEADER|URI|RBL):\s*/i, "")
            .trim();
    return out;
}

/**
 * What the server said, taken apart: the rules that fired, what each one is
 * evidence of, and whether the From line was proved independently.
 *
 * One mechanism, two consumers — the banner finding below and spamScoreOf's
 * chip — so the banner and the chip can never disagree about what a message is.
 */
interface ServerSpamAnalysis {
    score: number;
    threshold: number;
    flagged: boolean;
    rules: SpamRule[];
    /** Worst class that actually scored, forgery > association > other > bulk. */
    kind: "forgery" | "association" | "bulk" | "unclassified";
    /** The From line is proved — see provedBy for how. */
    proved: boolean;
    /** Which evidence proved it: the receiving server's DMARC verdict, a valid
     *  signature from the author's own domain, or the fact that the message
     *  never crossed an untrusted host. */
    provedBy: "dmarc" | "dkim" | "trusted" | "";
    provedDomain: string;
    provedPolicy: string;
    /** Every rule that SCORED is a bulk-mail or blocklist test — nothing that
     *  pushed this message up is about its content or its sender. Computed,
     *  not inferred from `kind`: `kind` is the WORST class present, so it says
     *  nothing about the rules underneath it. */
    bulkOnly: boolean;
    /** The reader's own trained filter put this at 0-1% spam. */
    bayesHam: boolean;
    /** Authentication the receiving server saw fail, by name: "SPF", "DKIM",
     *  "DMARC". Any of these is an identity finding on its own. */
    authFailures: string[];
    /** Top scoring rules, worst first, named for a human to judge in a second. */
    reasons: string[];
    /** The reader-listed intermediary that provably handled this message, or "". */
    intermediary: string;
    /** Domains whose DKIM signature verified — candidates for that list. */
    signers: string[];
    /** The mailing list that delivered this (List-Id), whether the server
     *  proved the list sent it, and whether the reader has listed it in
     *  `trustedLists`. `trusted` is true only when `proved` is. (2026-09-09) */
    list: { id: string; proved: boolean; host: string; trusted: boolean };
}

function analyzeServerSpam(input: TrustInput): ServerSpamAnalysis {
    const status = unfold(header(input.headerLines, "x-spam-status"));
    const flag = header(input.headerLines, "x-spam-flag");
    if (!status && !flag) return null;

    const score = Number(status.match(/score=(-?[\d.]+)/)?.[1]);
    const threshold = Number(status.match(/required=(-?[\d.]+)/)?.[1]);
    const flagged = /^yes/i.test(status) || /^yes/i.test(flag);

    const dmarc = dmarcProof(input);
    // A list the server proved delivered this message explains two things
    // that otherwise read as forgery: the author's signature no longer
    // verifying (the list tagged the subject and appended a footer), and the
    // From domain not matching the path (the list re-sent it from its own
    // host). Both are removed from the evidence on a PROVED list whether or
    // not the reader trusts it — the explanation is a fact about the
    // headers, and the trust is a separate question answered below. SPF and
    // DMARC failures stay: SPF is checked against the list's own envelope,
    // and a list that lets a DMARC failure through is not one to be quiet
    // about. (2026-09-09)
    const listFound = listProof(input);
    const trustedLists = (input.trustedLists || []).map(l => (l || "").trim().toLowerCase());
    const list = { ...listFound, trusted: listFound.proved && trustedLists.includes(listFound.id) };
    const authFailures = list.proved && !dmarc.failed ? dmarc.authFailures.filter(f => f !== "DKIM") : dmarc.authFailures;
    const report = parseSpamReport(header(input.headerLines, "x-spam-report"));
    // Everything up to the first lowercase key=value — SpamAssassin's trailing
    // shortcircuit=/autolearn=/version= fields. Cutting on "not a rule name"
    // rather than on whitespace is what keeps "...,WORD_INVIS shortcircuit=no"
    // from delivering a rule called WORD_INVISshortcircuit.
    const names = (status.match(/tests=([\s\S]*?)(?=\s+[a-z_]+=|$)/)?.[1] || "")
        .replace(/\s+/g, "").split(",").map(n => n.toUpperCase()).filter(Boolean);

    const rules: SpamRule[] = names.map(name => {
        const found = report.get(name);
        return {
            name,
            score: found ? found.score : NaN,
            description: found ? found.description : "",
            cls: classifyRule(name, dmarc.failed, list.proved),
        };
    });

    // A rule that scored zero or negative did not put this message over any
    // line, so it cannot be the reason the message is here. Unscored rules
    // (no X-Spam-Report, which is most unflagged mail) still count — absence
    // of a report is not evidence the rule was free.
    const scored = (r: SpamRule) => !(r.score <= 0);
    const present = (cls: RuleClass) => rules.some(r => r.cls === cls && scored(r));

    // An unknown rule keeps a proved sender at caution only if it could have
    // flagged the message BY ITSELF. Presence was too crude: an IEEE
    // conference announcement (Bob 2026-08-28) scored 4.5 of 4.5 on DCC 3.0 +
    // marketing 1.0 + 1.5 for GB_AWSTRACK_REDIR — Amazon SES's click-tracker,
    // which every bulk mailer on earth routes through — and the 1.5 alone
    // pushed a DMARC-proved announcement into "check this message before
    // acting on it". Meanwhile KAM_BENEFICIARY, the case this rule exists for,
    // is worth 10.0 on its own.
    //
    // The comparison is against the SERVER'S OWN threshold, so this adds no
    // constant to tune: "enough to condemn" already has a number, and it is
    // the one the server used. Unscored rules (no X-Spam-Report) make the sum
    // unknown, and unknown stays cautious.
    const unknown = rules.filter(r => r.cls === "other" && scored(r));
    const unknownWeight = unknown.reduce((sum, r) => sum + r.score, 0);
    const unknownCouldFlagAlone = !unknown.length ? false
        : !Number.isFinite(unknownWeight) || !Number.isFinite(threshold) ? true
        : unknownWeight >= threshold;
    // What the chip may claim about the rule set. "Every rule that scored is a
    // bulk-mail test" is a statement about ALL of them, so it has to be
    // checked against all of them — deriving it from `proved` (which says
    // nothing about the rules) or from `kind` (the worst class, which says
    // nothing about the rest) asserts something the code never looked at.
    // The Altis Hotels welcome mail, 2026-09-03 (Bob: "marked as spam but is
    // valid"): DMARC-proved, so the chip said "scored by bulk-mail or
    // blocklist rules" — with 3.2 of its 8.3 coming from KAM_ACCOUNTPHISH,
    // which is neither, listed directly underneath.
    const bulkOnly = rules.every(r => !scored(r) || r.cls === "bulk" || r.cls === "neutral");

    const kind = (authFailures.length || present("forgery")) ? "forgery"
        : present("association") ? "association"
        : unknownCouldFlagAlone ? "unclassified"
        : rules.length ? "bulk"
        : "unclassified";

    // Two proofs SpamAssassin states in its own rule list, which mail that
    // never crossed a trust boundary carries INSTEAD of an Authentication-Results
    // header — Bob's own message to John Levine about spam handling, 2026-08-27,
    // had neither AR nor DMARC and got a caution banner reading "links and
    // attachments here should not be opened" on prose he had just written:
    //
    //   ALL_TRUSTED    Passed through trusted hosts only via SMTP
    //   DKIM_VALID_AU  Message has a valid DKIM or DK signature from author's domain
    //
    // DKIM_VALID_AU is the same evidence DMARC alignment rests on — the author
    // domain signed it and the signature verified — so it proves the From line on
    // its own. ALL_TRUSTED says the message reached the mailbox without passing
    // through a host the server distrusts, which is Bob's own authenticated
    // submission; nothing could have injected it in transit. (What flagged his
    // message was URIBL_DBL_SPAM 8.0 on blueorchestra.org — the domain he was
    // quoting IN a discussion about spam. Guilt by association at its purest.)
    const has = (name: string) => rules.some(r => r.name === name);
    const provedBy = dmarc.pass ? "dmarc"
        : has("DKIM_VALID_AU") ? "dkim"
        : has("ALL_TRUSTED") ? "trusted"
        : "";
    const fromDomain = (bare(input.fromAddress).split("@")[1] || "").toLowerCase();

    const reasons = rules
        .filter(r => r.cls !== "neutral" && scored(r))
        .sort((a, b) => (b.score || 0) - (a.score || 0))
        .slice(0, 3)
        .map(r => {
            const num = Number.isFinite(r.score) ? `${r.score.toFixed(1)} ` : "";
            return r.description ? `${num}${r.name} (${r.description})` : `${num}${r.name}`;
        });

    return {
        score, threshold, flagged, rules, kind,
        proved: !!provedBy, provedBy, bulkOnly,
        provedDomain: dmarc.domain || fromDomain, provedPolicy: dmarc.policy,
        bayesHam: rules.some(r => r.name === "BAYES_00" || r.name === "BAYES_01"),
        authFailures,
        reasons,
        intermediary: provedIntermediary(input),
        signers: dkimSigners(input),
        list,
    };
}

/**
 * Did the receiving server's spam filter already say yes — and if so, to WHAT?
 *
 * The highest-value signal available, and it costs nothing: the mail server
 * ran SpamAssassin before delivery and wrote the answer into the message.
 * mailx was throwing it away.
 *
 * Only a POSITIVE verdict is trusted, never a negative one. Anyone can inject
 * `X-Spam-Flag: NO` into a message they send, and treating that as a clean
 * bill of health would hand attackers a switch for turning the check off. A
 * forged YES only flags the forger's own mail, so the asymmetry is safe.
 *
 * The severity comes from the RULES, never from the number (Claude Code
 * 2026-08-27):
 *   - a forgery rule fired          -> danger. This is what red is for.
 *   - the sender is DMARC-proved     -> info. The mail may well be unwanted,
 *     and every rule that scored is     but "not what it says it is" is a
 *     one this file can name, or        claim its own evidence contradicts.
 *     the whole score is bulk rules
 *   - anything else                  -> caution. Flagged, and the reason is
 *                                      not identity — or is a rule this file
 *                                      cannot classify, which is never a
 *                                      reason to go quiet.
 */
/** How the From line was proved, in the reader's terms — named so it can be
 *  checked against the headers rather than believed. */
function provedNote(a: ServerSpamAnalysis): string {
    switch (a.provedBy) {
        case "dmarc":
            return `DMARC pass, aligned${a.provedPolicy ? `, policy ${a.provedPolicy}` : ""}.`;
        case "dkim":
            return `Valid signature from ${a.provedDomain}, the sending domain itself (DKIM_VALID_AU).`;
        case "trusted":
            return "It passed through trusted hosts only (ALL_TRUSTED) — nothing untrusted handled it.";
        default:
            return "";
    }
}

function serverSpamVerdict(input: TrustInput): TrustFinding {
    const a = analyzeServerSpam(input);
    if (!a || !a.flagged) return null;

    // Mail from a sender the reader has approved, whose identity is proved,
    // and which nothing accuses of forgery: say nothing at all. The server's
    // score answers "is this bulk", the reader has already answered "is it
    // wanted", and repeating the first after the second is how a banner turns
    // into furniture. (Bob 2026-08-28, having just clicked "Always:
    // *@icecer.com" on a conference announcement: "I accepted the server so
    // shouldn't this be considered safe?")
    // A trusted INTERMEDIARY that provably handled the message answers the
    // same question the same way — Google Calendar's invitation on Aaron's
    // behalf is wanted because the reader said google.com is (2026-09-08).
    // A trusted mailing LIST answers it for every post the list provably
    // delivered, whoever wrote it — the From line is the list's word, not
    // the author's, and the reader subscribed to the list (2026-09-09).
    const trustedAndProved = (input.senderTrusted && a.proved) || !!a.intermediary || a.list.trusted;
    if (trustedAndProved && a.kind !== "forgery" && !a.authFailures.length) return null;
    // The party that sent this on the author's behalf, when it signed: the
    // Sender: header names it and a verified signature backs the claim. That
    // is the one the reader can sensibly vouch for as an intermediary.
    const senderDomain = (bare(header(input.headerLines, "sender")).split("@")[1] || "").toLowerCase();
    const relay = senderDomain ? a.signers.find(s => sameOrg(s, senderDomain)) : "";
    const via = relay && !a.proved ? { via: relay } : {};
    // Say WHICH party and HOW, in the finding itself (Bob 2026-09-08: "the
    // banner should tell which and how"). The action's label is quoted so
    // the reader can match it to the button — and to the allowlist.jsonc
    // entry it writes, `trustedIntermediaries`.
    const relayNote = relay && !a.proved
        ? `. Sent on the author's behalf by ${relay}, which signed it; "Trust ${relay} as an intermediary" stops this note for mail ${relay} signs.`
        : "";
    // The list that provably delivered this, named the same way, with the
    // action that writes `trustedLists`. Offered only while the author is
    // unproved and the list is not yet trusted: a proved author needs no
    // list, and a trusted list returned above.
    const listNote = a.list.proved && !a.list.trusted && !a.proved
        ? `. Delivered by the ${a.list.id} mailing list — ${a.list.host} proved it sent this — which re-sent the author's message from its own host, so nothing proves the From line; "Trust the ${a.list.id} list" stops this note for mail the list delivers.`
        : "";
    const viaList = listNote ? { list: a.list.id } : {};

    const numbers = Number.isFinite(a.score) && Number.isFinite(a.threshold)
        ? `SpamAssassin score ${a.score} of ${a.threshold}`
        : "flagged by SpamAssassin";
    const why = a.reasons.length ? ` — ${a.reasons.join("; ")}` : "";
    const bayes = a.bayesHam ? "; your own trained filter puts it at 0-1% spam (BAYES_00)" : "";

    if (a.kind === "forgery")
        return {
            id: "server-spam-verdict",
            severity: "danger",
            text: "Your mail server classified this as spam, and the rules that fired test who sent it.",
            detail: a.authFailures.length
                ? `${a.authFailures.join(" and ")} authentication failed at your server. ${numbers}${why}`
                : `${numbers}${why}`,
        };

    if (a.proved && a.kind !== "unclassified")
        return {
            id: "server-spam-verdict",
            severity: "info",
            text: a.provedBy === "trusted"
                ? "Your mail server scored this as spam, but it reached you without leaving your own systems."
                : `Your mail server scored this as spam, but ${a.provedDomain} proved it sent this.`,
            detail: `${provedNote(a)} ${numbers}${why}${bayes}`,
        };

    // Same discipline as the chip: "nothing that scored tests who sent it" is
    // a claim about every rule, so it is made only when every rule was
    // actually looked at and classified. `kind === "bulk"` does not establish
    // that — an unclassified rule that could not have flagged the message on
    // its own leaves the kind at bulk while remaining, by definition, a rule
    // this file cannot say anything about.
    if (a.kind === "bulk")
        return {
            id: "server-spam-verdict",
            severity: "info",
            text: a.bulkOnly
                ? "Your mail server rates this bulk mail — nothing that scored tests who sent it."
                : "Your mail server rates this bulk mail.",
            detail: `${numbers}${why}${bayes}`,
        };

    const proof = a.proved ? `${provedNote(a)} ` : "";
    if (input.senderTrusted && !a.proved)
        return {
            id: "server-spam-verdict",
            severity: "caution",
            text: "This claims to be a sender you trust, but nothing proves it is.",
            detail: `No DMARC pass, no valid signature from the sending domain, and it passed through hosts your server does not trust. ${numbers}${why}${listNote}`,
            ...viaList,
        };
    return {
        id: "server-spam-verdict",
        severity: "caution",
        text: "Your mail server classified this as spam before delivering it.",
        detail: `${proof}${numbers}${why}${bayes}${relayNote}${listNote}`,
        ...via,
        ...viaList,
    };
}

/**
 * Does the message claim to come from the reader themselves, while failing the
 * authentication that would prove it?
 *
 * Either half alone is ordinary — people really do mail themselves notes, and
 * forwarded mail really does fail SPF. Together they are not: mail genuinely
 * from your own account authenticates as your own account. This is the most
 * common shape of targeted phish, because a From line showing your own address
 * defeats every visual check a reader makes.
 *
 * Requires an Authentication-Results header to exist. A message without one
 * never crossed a trust boundary — it is your own mail, handled internally —
 * and is not evidence of anything. That case is the overwhelming majority:
 * 10,034 of the 10,338 self-claimed messages in a 50,000-message scan of Bob's
 * store had no such header at all.
 *
 * `spf=none` counts as "not authenticated", not just `fail`. A domain with no
 * SPF record published can never produce a failure, only a shrug — so treating
 * `none` as neutral leaves the least-protected domain the least defended.
 * bobf.frankston.com publishes no SPF, and that is precisely the hole the
 * 2026-08-25 sextortion mail walked through: SpamAssassin scored it 2.7
 * against a 4.5 threshold and delivered it. In that same 50,000-message scan
 * `spf=none` on self-claimed mail occurred 18 times and was spam nearly every
 * time ("Settle your debt", "AAA Final notice", "McAfee Protection Plan
 * Ended"); legitimate self-mail was in the no-header group, untouched by this.
 */
function selfSpoof(input: TrustInput): TrustFinding {
    const from = bare(input.fromAddress);
    if (!from) return null;
    const own = new Set(input.ownAddresses.map(bare).filter(Boolean));
    if (!own.has(from)) return null;

    const auth = headerAll(input.headerLines, "authentication-results").join(" ; ");
    if (!auth) return null;
    // The From domain passed DMARC, aligned, at the receiving server: the
    // message provably came from that domain, which is the opposite of a
    // spoof. Checked BEFORE the failure scan because the scan reads every
    // Authentication-Results and every signature — an ISOC list post
    // (2026-09-09) carried dkim=pass d=elists.isoc.org, dmarc=pass
    // header.from=elists.isoc.org, AND dkim=fail for the original author's
    // standardstrack.com signature the list had broken, and that one fail
    // painted the whole message red as "did not come from your account"
    // (the list address was the Delivered-To, so it counted as Bob's own).
    // One failed signature on a message with an aligned pass is not
    // evidence about the From line; the pass is.
    //
    // The same proof without the dmarc= verdict: a From domain that publishes
    // no DMARC policy gets no dmarc=pass, but its own SPF pass or its own
    // verified signature is the very evidence DMARC would have aligned on.
    // The AMW list (berglist.com, 2026-09-09): spf=pass for
    // amw-bounces@berglist.com, dkim=pass d=berglist.com, and dkim=fail for
    // the gmail.com author signature the list broke — flagged as a spoof of
    // the list's own address.
    if (dmarcProof(input).pass || alignedPass(input)) return null;
    const failed = [
        /\bspf=(fail|softfail)\b/i.test(auth) ? "SPF" : "",
        /\bdkim=(fail|permerror)\b/i.test(auth) ? "DKIM" : "",
        /\bdmarc=(fail|permerror)\b/i.test(auth) ? "DMARC" : "",
    ].filter(Boolean);
    // Nothing failed, but did anything actually PASS? "spf=none dkim=none" is
    // not a clean bill of health for a message claiming to be you.
    if (!failed.length) {
        if (/\b(spf|dkim|dmarc)=pass\b/i.test(auth)) return null;
        const envelopeAddr = bare(header(input.headerLines, "return-path"));
        const note = envelopeAddr && envelopeAddr !== from ? `; envelope sender ${envelopeAddr}` : "";
        return {
            id: "self-spoof",
            severity: "danger",
            text: `This claims to be from your own address, ${from}, and nothing proves it came from your account.`,
            detail: `no SPF, DKIM or DMARC result to check against${note} — your domain publishes no SPF record, so forgery cannot be detected by the sending side`,
        };
    }

    // Name the envelope sender when it disagrees — that is the address that
    // actually sent this, and seeing it beside the claim is the whole story.
    const envelope = bare(header(input.headerLines, "return-path"));
    const envelopeNote = envelope && envelope !== from ? `; actually sent by ${envelope}` : "";
    return {
        id: "self-spoof",
        severity: "danger",
        text: `This claims to be from your own address, ${from}, but it did not come from your account.`,
        detail: `${failed.join(" and ")} authentication failed${envelopeNote}`,
    };
}

/** Bigger than any real image. An honest layout never needs a hotspot this
 *  large; a forger picks an absurd number so they don't have to know the
 *  rendered size. Not a tuned threshold — a sanity bound. */
const OVERLAY_MIN_EDGE_PX = 2000;

/**
 * Is an entire image covered by one invisible link?
 *
 * `<area coords="0,0,10000,10000">` turns every pixel of an image into a link
 * with no underlined text, no visible URL, and no safe area to click. Real
 * mail uses image maps almost never, and never at that size.
 */
function hiddenLinkOverlay(bodyHtml: string): TrustFinding {
    for (const m of bodyHtml.matchAll(/<area\b[^>]*>/gi)) {
        const tag = m[0];
        const href = tag.match(/href\s*=\s*["']([^"']+)["']/i)?.[1];
        if (!href) continue;
        const coords = (tag.match(/coords\s*=\s*["']([^"']+)["']/i)?.[1] || "")
            .split(/[,\s]+/).map(Number).filter(n => Number.isFinite(n));
        if (coords.length < 4) continue;
        const width = Math.abs(coords[2] - coords[0]);
        const height = Math.abs(coords[3] - coords[1]);
        if (width < OVERLAY_MIN_EDGE_PX || height < OVERLAY_MIN_EDGE_PX) continue;
        let host = href;
        try { host = new URL(href).hostname; } catch { /* keep the raw href */ }
        return {
            id: "hidden-link-overlay",
            severity: "danger",
            text: "The picture in this message is one large hidden link — clicking anywhere on it goes to the same place.",
            detail: `${width}x${height} clickable area over the image, pointing at ${host}`,
        };
    }
    return null;
}

/** The domain the receiving server proved the From line for — "" when nothing
 *  did. One place answers "is this sender who they say they are", so the
 *  banner, the chip and the link checks can never disagree about it.
 *
 *  dmarcProof is asked first because it needs no SpamAssassin: an
 *  Authentication-Results header alone is enough, which is what mail arriving
 *  through the Gmail API carries. analyzeServerSpam adds the two proofs
 *  SpamAssassin states in its own rule list instead (DKIM_VALID_AU,
 *  ALL_TRUSTED) and returns null when the server ran no filter at all. */
function provedSenderDomain(input: TrustInput): string {
    const fromDomain = (bare(input.fromAddress).split("@")[1] || "").toLowerCase();
    if (!fromDomain) return "";
    if (dmarcProof(input).pass) return fromDomain;
    return analyzeServerSpam(input)?.proved ? fromDomain : "";
}

/**
 * Does a link route through a redirector carrying its real destination in the
 * query string?
 *
 * `castanet-sa.fr/modules/babel/redirect.php?newurl=https%3A%2F%2Fevil.example`
 * borrows a real site's reputation: the visible host is a legitimate business
 * whose redirect script was left open, and the destination appears only
 * percent-encoded inside a parameter. Reporting the ENDPOINT is the point — a
 * reader cannot be expected to URL-decode a query string.
 *
 * Laundering needs a SECOND PARTY — someone whose reputation is being spent
 * without their consent. Two ways a link has none, both checked below, and
 * neither of them a threshold:
 *
 *   1. The redirector and the destination are the same organisation. A site
 *      bouncing through its own hostnames is routing, not hiding.
 *   4. The link ENDS at the proved sender (2026-09-08). Microsoft 365's
 *      Message center mail, dmarc=pass header.from=microsoft.com with
 *      policy reject, links through its click-tracker
 *      `nam.safelink.emails.azure.net` to `admin.microsoft.com`: two
 *      organisations by hostname, nobody signed for azure.net, and the
 *      sender is not the redirector — rules 1 to 3 all miss (Bob: "another
 *      safe message flagged"). But the reader lands on the site of the party
 *      that provably sent the mail, which is exactly where a plain link
 *      would have taken them; the hop hides nothing from the reader. The
 *      redirector's reputation may be borrowed, but that is the
 *      redirector's problem, not this reader's — and the sender who chose
 *      the route is the proved sender, who could have linked openly.
 *   2. The redirector IS the proved sender. Google's account-security notice
 *      (2026-09-02, Bob: "this is valid") links through
 *      `accounts.google.com/AccountChooser?continue=https://myaccount.google.com/…`
 *      — two hostnames that are neither equal nor sub/parent of each other,
 *      so rule 1 alone does not save it. But the receiving server recorded
 *      `dmarc=pass header.from=accounts.google.com`: the host in the visible
 *      link belongs to the party that provably sent the mail, spending its
 *      own reputation. That the sender might itself be malicious is a
 *      different question than the one this check asks.
 *
 * The proof has to come from the topmost Authentication-Results, which the
 * sender cannot write — otherwise the escape is a switch a forger flips by
 * typing a domain into From. No proof means the check stays on.
 */
function redirectorLink(input: TrustInput): TrustFinding {
    const bodyHtml = input.bodyHtml || "";
    const senderDomain = provedSenderDomain(input);
    // 3. (2026-09-08) The redirector belongs to a reader-listed intermediary
    //    that provably handled the message. Google Calendar wraps every link
    //    in an invitation as google.com/url?q=…, signs the mail d=google.com,
    //    and is not the From — rule 2 cannot save it, and the reader's answer
    //    is "I trust google.com to do that". Same second-party logic: the
    //    host spending its reputation is the host that signed.
    const intermediary = provedIntermediary(input);
    const signers = dkimSigners(input);
    for (const m of bodyHtml.matchAll(/href\s*=\s*["']([^"']+)["']/gi)) {
        let url: URL;
        try { url = new URL(m[1]); } catch { continue; }
        if (!/^https?:$/.test(url.protocol)) continue;
        if (senderDomain && sameOrg(url.hostname, senderDomain)) continue;  // the sender's own host
        if (intermediary && sameOrg(url.hostname, intermediary)) continue;  // a trusted relay's own host
        for (const [, value] of url.searchParams) {
            let target: URL;
            try { target = new URL(value); } catch { continue; }
            if (!/^https?:$/.test(target.protocol)) continue;
            if (sameOrg(target.hostname, url.hostname)) continue;   // same site, not laundering
            if (senderDomain && sameOrg(target.hostname, senderDomain)) continue;  // ends at the proved sender (rule 4)
            // Name the signer behind this redirector, if there is one, so the
            // viewer can offer "trust google.com as an intermediary" — and
            // only then: a redirector nobody signed for has no party to vouch
            // for, and a list entry could not clear it anyway.
            const signer = signers.find(s => sameOrg(s, url.hostname));
            // Which party, and how — or why there is no party (Bob 2026-09-08).
            const how = signer
                ? ` ${signer} signed this message; "Trust ${signer} as an intermediary" stops this warning for links through ${signer} in mail it signs.`
                : ` Nobody signed this message for ${url.hostname}, so there is no intermediary to trust; only the sender could vouch for the link.`;
            return {
                id: "redirector-link",
                severity: "caution",
                text: `A link hides where it goes: it passes through ${url.hostname} and ends at ${target.hostname}.`,
                detail: `${url.hostname} -> ${target.hostname}.${how}`,
                ...(signer ? { via: signer } : {}),
            };
        }
    }
    return null;
}

/**
 * Everything findable about this message, worst first.
 *
 * Returns an empty array for ordinary mail — the caller then shows nothing,
 * rather than a reassuring "looks fine" this cannot support. Absence of
 * evidence is not a clean bill of health.
 */
export function assessMessageTrust(input: TrustInput): TrustFinding[] {
    const findings = [
        serverSpamVerdict(input),
        selfSpoof(input),
        relayAuthMismatch(input),
        zeroWidthObfuscation(input.bodyText || ""),
        hiddenLinkOverlay(input.bodyHtml || ""),
        redirectorLink(input),
    ].filter(Boolean);
    const rank = { danger: 0, caution: 1, info: 2 };
    return findings.sort((a, b) => rank[a.severity] - rank[b.severity]);
}

/**
 * Did the relay record a different account than the one the message claims?
 *
 * Shared hosts stamp the identity that actually authenticated to submit the
 * message. That stamp is added by the RELAY, downstream of whoever sent it, so
 * a forger cannot remove or edit it — which makes disagreement between the
 * stamp and the From line about as close to proof as mail headers get.
 *
 * The 2026-08-25 sextortion mail (Bob: "note that the origin is different than
 * the from domain in antiabuse and in source-auth"):
 *
 *   From:            universe@bobf.frankston.com
 *   X-Source-Auth:   admin@calebjross.com
 *   X-Source-Cap:    (base64) calebjro;calebjro;box2202.bluehost.com
 *   Message-ID:      <...@calebjross.com>
 *
 * A Bluehost cPanel account belonging to an unrelated domain authenticated and
 * sent mail wearing Bob's address. SpamAssassin scored the whole thing 2.7
 * against a 4.5 threshold and let it through.
 *
 * Compared at the registrable-domain level, not the full address: a host that
 * legitimately sends for a domain authenticates as some account AT that
 * domain, and the local part varies for perfectly ordinary reasons.
 */
const RELAY_IDENTITY_HEADERS = [
    "x-source-auth",           // cPanel / Exim (Bluehost, HostGator, most shared hosts)
    "x-authenticated-sender",  // Postfix / Communigate
    "x-auth-id",
    "x-authenticated-user",
];

function relayAuthMismatch(input: TrustInput): TrustFinding {
    const from = bare(input.fromAddress);
    const fromDomain = from.split("@")[1] || "";
    if (!fromDomain) return null;

    for (const name of RELAY_IDENTITY_HEADERS) {
        const stamped = bare(header(input.headerLines, name));
        const stampedDomain = stamped.split("@")[1] || "";
        // Sub/parent-domain is the same organisation — mail.example.com
        // sending for example.com is ordinary. Cross-organisation is not.
        if (!stampedDomain || sameOrg(stampedDomain, fromDomain)) continue;
        return {
            id: "relay-auth-mismatch",
            severity: "danger",
            text: `The account that actually sent this belongs to someone else: ${stampedDomain}, not ${fromDomain}.`,
            detail: `the sending server recorded ${stamped} as the authenticated sender, while the message claims to be from ${from}`,
        };
    }
    return null;
}

/**
 * Is the text stuffed with invisible characters to break word matching?
 *
 * Zero-width space, zero-width non-joiner, zero-width joiner and the
 * zero-width no-break space render as nothing, so a reader sees plain prose
 * while every filter matching on words sees gibberish. The 2026-08-25
 * sextortion mail put one between nearly every pair of letters — "i regret to
 * inform you" reached the parser as `i re‍g﻿ret t‌o ‌in​f‌or﻿m​ you`. SpamAssassin
 * spotted it (UNICODE_OBFU_ZW_MANY) and still scored the message 2.7 against a
 * 4.5 threshold, so it was delivered.
 *
 * Counted only BETWEEN TWO LATIN LETTERS, which is what obfuscation looks like
 * and what the legitimate uses do not: an emoji ZWJ sequence joins pictographs,
 * Arabic and Indic ZWNJ sit between their own scripts' letters, and a soft
 * hyphen marks a break opportunity rather than splitting a word mid-render.
 *
 * The count guards against a single stray character surviving a copy-paste out
 * of a web page — it is a sanity bound, not a tuned dial. Prose does not
 * accumulate dozens of these by accident; the sample message had over 900.
 */
const ZERO_WIDTH_MIN_OCCURRENCES = 12;

function zeroWidthObfuscation(bodyText: string): TrustFinding {
    if (!bodyText) return null;
    const between = bodyText.match(/[A-Za-z][​‌‍﻿][A-Za-z]/g);
    const count = between ? between.length : 0;
    if (count < ZERO_WIDTH_MIN_OCCURRENCES) return null;
    return {
        id: "zero-width-obfuscation",
        severity: "danger",
        text: "The words in this message are stuffed with invisible characters to get past spam filters.",
        detail: `${count} zero-width characters hidden inside words — legitimate mail has no reason to do this`,
    };
}

/**
 * The receiving server's spam score, whether or not it crossed the threshold.
 *
 * Separate from the findings on purpose. A finding is an accusation and only
 * fires on evidence; this is a measurement, and the reader asked to see the
 * number itself (Bob 2026-08-25: "should we have a flag ... with the spam
 * assassin number and a color code?"). The sextortion mail is exactly why:
 * 2.7 against a 4.5 threshold is not "clean", it is "did not quite trip the
 * wire", and those are very different things to a person deciding whether to
 * trust a message.
 *
 * Returns null when the server left no score — most mail from providers that
 * filter server-side and say nothing in the headers.
 */
export interface SpamScore {
    score: number;
    threshold: number;
    /** The server's own yes/no, which is simply score >= threshold. */
    flagged: boolean;
    /** What the rules that actually scored are evidence OF. `bulk` and a
     *  `proved` sender are the two ways a high number can be honest mail —
     *  the chip stays visible and says so, rather than going red at a number
     *  and teaching the reader to ignore it. (Claude Code 2026-08-27) */
    kind: "forgery" | "association" | "bulk" | "unclassified";
    /** The From line is proved — provedBy says by what. */
    proved: boolean;
    /** Which evidence: the server's DMARC verdict, a valid signature from the
     *  author's own domain, or never having crossed an untrusted host. The
     *  chip's tooltip has to say which one, or it asserts a DMARC pass that
     *  may not exist (Bob's own mail carries no Authentication-Results at
     *  all). */
    provedBy: "dmarc" | "dkim" | "trusted" | "";
    /** Every rule that scored is a bulk-mail or blocklist test. The chip's
     *  tooltip may say so ONLY when this is true — it is a claim about all of
     *  the rules, and the reader can read them underneath it. */
    bulkOnly: boolean;
    /** Top scoring rules, named — "6.0 URIBL_SBL (Contains an URL's NS IP
     *  listed in the Spamhaus SBL blocklist)". A number alone is unactionable;
     *  this can be judged in a second. */
    reasons: string[];
    /** The reader approved this sender AND the sender is proved: the score is
     *  still true, and no longer worth showing. The viewer hides the chip. */
    trusted: boolean;
    /** The mailing list that delivered this (List-Id), or "". With
     *  `listProved` — the receiving server recorded the list's host sending
     *  it — the viewer can offer "Trust the ‹list› list"; `listTrusted` says
     *  the reader already has. (2026-09-09) */
    listId: string;
    listProved: boolean;
    listTrusted: boolean;
}

export function spamScoreOf(input: TrustInput): SpamScore {
    const a = analyzeServerSpam(input);
    if (!a || !Number.isFinite(a.score) || !Number.isFinite(a.threshold)) return null;
    return {
        score: a.score,
        threshold: a.threshold,
        flagged: a.flagged,
        kind: a.kind,
        proved: a.proved,
        provedBy: a.provedBy,
        bulkOnly: a.bulkOnly,
        trusted: ((!!input.senderTrusted && a.proved) || !!a.intermediary || a.list.trusted) && a.kind !== "forgery" && !a.authFailures.length,
        reasons: a.reasons,
        listId: a.list.id,
        listProved: a.list.proved,
        listTrusted: a.list.trusted,
    };
}
